import { useEffect, useState, type CSSProperties } from 'react'
import { Button, Card, Group, NativeSelect, Select, Stack, Text } from '@mantine/core'
import { IconRefresh } from '@/components/icons'
import type { DashboardPeriodKey } from '@/lib/dashboard-period'
import type { ProjectRow } from '@/types/api'

const PERIOD_DATA: { value: DashboardPeriodKey; label: string }[] = [
  { value: '1h', label: 'За час' },
  { value: 'today', label: 'За сегодня' },
  { value: '24h', label: 'За сутки' },
  { value: '7', label: 'За неделю' },
  { value: '30', label: 'За 30 дней' },
  { value: '90', label: 'За 90 дней' },
  { value: 'custom_datetime', label: 'Свой период' },
]

const DATETIME_INPUT_STYLE: CSSProperties = {
  padding: '4px 8px',
  borderRadius: 6,
  border: '1px solid var(--mantine-color-default-border)',
  background: 'var(--mantine-color-body)',
  color: 'var(--mantine-color-text)',
  fontSize: 12,
  minHeight: 30,
}

export function AnalyticsPeriodFilters({
  period,
  onPeriodChange,
  projectIds,
  onProjectIdsChange,
  projects,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  customApplied,
  onCustomApply,
  onRefresh,
  loading,
}: {
  period: DashboardPeriodKey
  onPeriodChange: (p: DashboardPeriodKey) => void
  projectIds: string[]
  onProjectIdsChange: (ids: string[]) => void
  projects: ProjectRow[]
  customFrom: string
  customTo: string
  onCustomFromChange: (v: string) => void
  onCustomToChange: (v: string) => void
  /** False until «Показать» after selecting «Свой период» (or after editing dates). */
  customApplied: boolean
  onCustomApply: () => void
  onRefresh: () => void
  loading: boolean
}) {
  // Draft dates stay local until «Показать». Selecting «Свой период» also waits
  // for that button — otherwise defaults would fire a heavy request immediately.
  const [draftFrom, setDraftFrom] = useState(customFrom)
  const [draftTo, setDraftTo] = useState(customTo)
  useEffect(() => setDraftFrom(customFrom), [customFrom])
  useEffect(() => setDraftTo(customTo), [customTo])
  const draftChanged = draftFrom !== customFrom || draftTo !== customTo
  const draftInvalid = !draftFrom || !draftTo || new Date(draftFrom) >= new Date(draftTo)
  const canApply = !draftInvalid && (!customApplied || draftChanged)
  const applyDraft = () => {
    if (!canApply) return
    onCustomFromChange(draftFrom)
    onCustomToChange(draftTo)
    onCustomApply()
  }

  return (
    <Card withBorder padding="sm" radius="md">
      <Stack gap="xs">
        <Group align="center" wrap="wrap" gap="sm">
          <Text size="xs" fw={600} c="dimmed" style={{ marginRight: 2 }}>
            Фильтры
          </Text>
          <Group gap={6} wrap="wrap" align="center">
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Период
            </Text>
            <NativeSelect
              size="xs"
              aria-label="Период"
              data={PERIOD_DATA}
              value={period}
              onChange={(e) => onPeriodChange(e.currentTarget.value as DashboardPeriodKey)}
              styles={{ input: { minHeight: 30, minWidth: 132 } }}
            />
          </Group>
          <Group gap={6} wrap="wrap" align="center">
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Проект
            </Text>
            <Select
              size="xs"
              aria-label="Проект"
              data={projects.map((pr) => ({ value: pr.id, label: pr.name || pr.slug || pr.id }))}
              value={projectIds[0] || null}
              onChange={(value) => onProjectIdsChange(value ? [value] : [])}
              styles={{ input: { minHeight: 30, minWidth: 140, maxWidth: 240 } }}
              placeholder="Все проекты"
              searchable
              clearable
              nothingFoundMessage="Нет"
            />
          </Group>
          <Button
            size="xs"
            leftSection={<IconRefresh size={14} stroke={1.5} />}
            variant="light"
            onClick={onRefresh}
            loading={loading}
            disabled={period === 'custom_datetime' && !customApplied}
          >
            Обновить
          </Button>
        </Group>
        {period === 'custom_datetime' ? (
          <Group align="center" wrap="wrap" gap="sm">
            <Group gap={6} align="center" wrap="nowrap">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                От
              </Text>
              <input
                type="datetime-local"
                aria-label="От (дата и время)"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyDraft()
                }}
                style={DATETIME_INPUT_STYLE}
              />
            </Group>
            <Group gap={6} align="center" wrap="nowrap">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                До
              </Text>
              <input
                type="datetime-local"
                aria-label="До (дата и время)"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyDraft()
                }}
                style={DATETIME_INPUT_STYLE}
              />
            </Group>
            <Button size="xs" onClick={applyDraft} disabled={!canApply}>
              Показать
            </Button>
            {draftFrom && draftTo && draftInvalid ? (
              <Text size="xs" c="red">
                Начало периода должно быть раньше конца
              </Text>
            ) : null}
            {!customApplied && !draftInvalid ? (
              <Text size="xs" c="dimmed">
                Нажмите «Показать», чтобы загрузить данные
              </Text>
            ) : null}
          </Group>
        ) : null}
      </Stack>
    </Card>
  )
}
